import {
  BaseTimetableGrid,
  ClassScheduleRow,
  ClassScheduleSettings,
  WeekdayKey,
  WeekdayMaxPeriod,
} from '../services/sectionLayoutService';
import {
  AnnualTimetableCell,
  AnnualTimetableCellUpsert,
  AnnualTimetableSlotKind,
  AnnualWeekdayKey,
  DayKind,
  Grade,
} from '../types/domain';
import { addDays, parseISODate, toISODate } from './date';
import type { WeekInfo } from './programProgressRows';

export const MAX_ANNUAL_TIMETABLE_SLOTS = 10;

/** 단축·증배 반영 시 '마지막 수업 교시' 뒤 칸을 표시하기 위해 memo에 저장하는 내부 표식 */
export const ANNUAL_SHORT_DAY_TAIL_MEMO = '__CP_SHORT_DAY_TAIL__';

export function isAnnualShortDayTailMemo(memo: string): boolean {
  return (memo ?? '').trim() === ANNUAL_SHORT_DAY_TAIL_MEMO;
}

export const ANNUAL_WEEKDAY_KEYS: Array<{ key: AnnualWeekdayKey; label: string }> = [
  { key: 'mon', label: '월' },
  { key: 'tue', label: '화' },
  { key: 'wed', label: '수' },
  { key: 'thu', label: '목' },
  { key: 'fri', label: '금' },
  { key: 'sat', label: '토' },
];

export interface AnnualClassEntry {
  gradeId: string;
  label: string;
  sectionId: string;
  sectionRows: ClassScheduleRow[];
  slotLimitByDay: WeekdayMaxPeriod;
}

export interface AnnualEffectiveCell {
  key: string;
  id?: string;
  workspaceId: string;
  gradeId: string;
  academicYear: number;
  termNumber: 1 | 2;
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey;
  slotIndex: number;
  dateIso: string;
  textValue: string;
  memo: string;
  colorHex: string;
  slotKind: AnnualTimetableSlotKind;
  /** `non_class`일 때만: 사용자 지정 vs 학사일정·요일 시수에 따른 자동 비수업 */
  nonClassSource: 'user' | 'calendar_schedule' | null;
  isEditable: boolean;
  isDedicated: boolean;
  isBaseExcluded: boolean;
}

/** 화면·저장용 슬롯 종류 — DB에 남은 예전 `holiday`/`out_of_term`/`non_class` 잔재를 무시 */
export function resolveAnnualDisplaySlotKind(
  seedKind: AnnualTimetableSlotKind,
  storedKind?: AnnualTimetableSlotKind
): { slotKind: AnnualTimetableSlotKind; nonClassSource: 'user' | 'calendar_schedule' | null } {
  if (seedKind === 'holiday' || seedKind === 'out_of_term') {
    return { slotKind: seedKind, nonClassSource: null };
  }
  const stored = storedKind;
  if (stored === 'holiday' || stored === 'out_of_term') {
    return { slotKind: seedKind, nonClassSource: null };
  }
  // 저장값이 있으면(사용자 버튼 적용 포함) holiday/out_of_term 외에는 seed보다 우선한다.
  if (stored) {
    if (stored === 'non_class') {
      return { slotKind: 'non_class', nonClassSource: 'user' };
    }
    return { slotKind: stored, nonClassSource: null };
  }
  if (seedKind === 'non_class') {
    return { slotKind: 'non_class', nonClassSource: 'calendar_schedule' };
  }
  return { slotKind: seedKind, nonClassSource: null };
}

/** DB upsert 시 학사일정·편제 자동 비수업은 `class` 빈 칸으로 정리 */
export function annualPersistSlotKindFromEffective(cell: Pick<AnnualEffectiveCell, 'slotKind' | 'nonClassSource'>): AnnualTimetableSlotKind {
  if (cell.slotKind === 'holiday' || cell.slotKind === 'out_of_term') {
    return cell.slotKind;
  }
  if (cell.slotKind === 'non_class' && cell.nonClassSource === 'calendar_schedule') {
    return 'class';
  }
  return cell.slotKind;
}

export type AnnualCalendarReconcileTermBlock = {
  termNumber: 1 | 2;
  weeks: WeekInfo[];
  startsOn: string;
  endsOn: string;
};

/** 학사일정 변경 후 DB·로컬에 남은 예전 `holiday`/`out_of_term`/자동 `non_class` 흔적 키 */
export function listAnnualKeysWithStaleCalendarSlotOverrides(params: {
  classEntries: AnnualClassEntry[];
  termBlocks: AnnualCalendarReconcileTermBlock[];
  holidaySet: Set<string>;
  calendarDayKindByDate?: Record<string, DayKind>;
  storedSlotKinds: Record<string, AnnualTimetableSlotKind>;
  isSchoolBreakDate?: (dateIso: string) => boolean;
}): string[] {
  const { classEntries, termBlocks, holidaySet, calendarDayKindByDate, storedSlotKinds, isSchoolBreakDate } = params;
  const stale: string[] = [];
  classEntries.forEach((entry) => {
    termBlocks.forEach(({ weeks, startsOn, endsOn }) => {
      weeks.forEach((week) => {
        ANNUAL_WEEKDAY_KEYS.forEach(({ key }, weekdayIdx) => {
          const weekdayKey = key as AnnualWeekdayKey;
          const dateIso = week.datesByWeekday[weekdayIdx] ?? '';
          for (let slotIndex = 0; slotIndex < MAX_ANNUAL_TIMETABLE_SLOTS; slotIndex += 1) {
            const compoundKey = buildAnnualCellKey(entry.gradeId, week.mondayIso, weekdayKey, slotIndex);
            const storedKind = storedSlotKinds[compoundKey];
            if (!storedKind) continue;
            const seedKind = getSeedSlotKind({
              dateIso,
              weekdayKey,
              slotIndex,
              sectionRows: entry.sectionRows,
              slotLimitByDay: entry.slotLimitByDay,
              startsOn,
              endsOn,
              holidaySet,
              calendarDayKindByDate,
              isSchoolBreakDate,
            });
            const { slotKind: displayKind, nonClassSource } = resolveAnnualDisplaySlotKind(seedKind, storedKind);
            if (displayKind !== storedKind) {
              stale.push(compoundKey);
              continue;
            }
            if (nonClassSource === 'calendar_schedule' && storedKind === 'non_class') {
              stale.push(compoundKey);
            }
          }
        });
      });
    });
  });
  return stale;
}

export interface AnnualProgramGrouping {
  classCells: AnnualEffectiveCell[];
  activityCells: AnnualEffectiveCell[];
  nonClassCells: AnnualEffectiveCell[];
  dedicatedCells: AnnualEffectiveCell[];
  baseExcludedCells: AnnualEffectiveCell[];
  byTextValue: Record<string, AnnualEffectiveCell[]>;
}

function parseClassName(raw: string): { title: string; grades: number[] } {
  const [base, rest] = raw.split('::');
  if (!rest) return { title: base || raw, grades: [] };
  const nums = rest
    .split(',')
    .map((v) => Number(v))
    .filter((n) => Number.isInteger(n) && n >= 1 && n <= 6);
  return { title: base || raw, grades: nums };
}

function formatClassLabel(raw: string): string {
  const parsed = parseClassName(raw);
  if (parsed.grades.length === 0) return parsed.title;
  return `${parsed.title} (${parsed.grades.join(',')}학년)`;
}

export function buildAnnualCellKey(
  gradeId: string,
  mondayIso: string,
  weekdayKey: string,
  slotIndex: number
): string {
  return `${gradeId}|${mondayIso}-${weekdayKey}-${slotIndex}`;
}

export function gradeIdFromAnnualCellKey(compoundKey: string): string | null {
  const index = compoundKey.indexOf('|');
  if (index < 0) return null;
  return compoundKey.slice(0, index);
}

/** `buildAnnualCellKey` 역파싱 (형식이 맞지 않으면 null) */
export function parseAnnualCellKey(compoundKey: string): {
  gradeId: string;
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey;
  slotIndex: number;
} | null {
  const pipe = compoundKey.indexOf('|');
  if (pipe < 0) return null;
  const gradeId = compoundKey.slice(0, pipe);
  const rest = compoundKey.slice(pipe + 1);
  const m = rest.match(/^(\d{4}-\d{2}-\d{2})-(mon|tue|wed|thu|fri|sat)-(\d+)$/);
  if (!m) return null;
  const slotIndex = Number(m[3]);
  if (!Number.isInteger(slotIndex) || slotIndex < 0) return null;
  return {
    gradeId,
    mondayIso: m[1],
    weekdayKey: m[2] as AnnualWeekdayKey,
    slotIndex,
  };
}

/** 해당 주의 월요일 + 요일 키 → 그날짜 ISO */
export function dateIsoForMondayWeekday(mondayIso: string, weekdayKey: AnnualWeekdayKey): string {
  const mon = parseISODate(mondayIso);
  if (Number.isNaN(mon.getTime())) return '';
  const idx = ANNUAL_WEEKDAY_KEYS.findIndex((w) => w.key === weekdayKey);
  const offset = idx >= 0 ? idx : 0;
  return toISODate(addDays(mon, offset));
}

export function buildAnnualClassEntries(
  schedule: ClassScheduleSettings | null,
  grades: Grade[]
): AnnualClassEntry[] {
  if (!schedule) return [];
  const gradeMap = new Map(grades.map((grade) => [grade.id, grade]));
  const entries: AnnualClassEntry[] = [];
  schedule.sections.forEach((section) => {
    const slotLimitByDay = schedule.sectionWeekdayMax[section.id] ?? {
      mon: 0,
      tue: 0,
      wed: 0,
      thu: 0,
      fri: 0,
    };
    const sectionRows = schedule.sectionRows[section.id] ?? [];
    section.gradeIds.forEach((gradeId) => {
      const grade = gradeMap.get(gradeId);
      if (!grade || entries.some((entry) => entry.gradeId === gradeId)) return;
      entries.push({
        gradeId,
        label: formatClassLabel(grade.name),
        sectionId: section.id,
        sectionRows,
        slotLimitByDay,
      });
    });
  });
  return entries;
}

export function buildAnnualPeriodLabels(rows: ClassScheduleRow[]): string[] {
  const labels: string[] = [];
  let classCount = 0;
  let afterSchoolCount = 0;
  for (let idx = 0; idx < MAX_ANNUAL_TIMETABLE_SLOTS; idx += 1) {
    const row = rows[idx];
    if (!row) {
      labels.push('');
      continue;
    }
    const stored = (row.name ?? '').trim();
    if (row.kind === 'class') {
      classCount += 1;
      labels.push(stored || `${classCount}교시`);
      continue;
    }
    if (row.kind === 'after_school') {
      afterSchoolCount += 1;
      labels.push(stored || `방과후${afterSchoolCount}`);
      continue;
    }
    labels.push(stored || '활동');
  }
  return labels;
}

/**
 * 기초 시간표 등: 요일별 교시 수(`class` 행만 카운트)와 맞추기 위해 `activity`·`after_school` 행은 건너뛰고
 * `class` 행만 순서대로 라벨을 만듭니다. 길이는 연간 슬롯 상한과 동일하게 패딩합니다.
 */
export function buildBaseTimetablePeriodLabels(rows: ClassScheduleRow[]): string[] {
  const labels: string[] = [];
  let classCount = 0;
  for (let idx = 0; idx < rows.length; idx += 1) {
    const row = rows[idx];
    if (!row) break;
    if (row.kind !== 'class') continue;
    classCount += 1;
    const stored = (row.name ?? '').trim();
    labels.push(stored || `${classCount}교시`);
    if (labels.length >= MAX_ANNUAL_TIMETABLE_SLOTS) break;
  }
  while (labels.length < MAX_ANNUAL_TIMETABLE_SLOTS) {
    labels.push('');
  }
  return labels;
}

/**
 * `sectionRows[slotIndex]`가 수업(`class`)일 때, 기초 시간표 `BaseTimetableGrid[요일][i]`에 대응하는 0-based `i`.
 * `class`가 아니면 -1.
 */
export function baseGridClassPeriodIndexForSectionSlot(
  sectionRows: ClassScheduleRow[],
  slotIndex: number
): number {
  if (sectionRows[slotIndex]?.kind !== 'class') return -1;
  let n = -1;
  for (let i = 0; i <= slotIndex; i += 1) {
    if (sectionRows[i]?.kind === 'class') n += 1;
  }
  return n;
}

export function getSlotLimitForDay(
  slotLimitByDay: WeekdayMaxPeriod,
  weekdayKey: AnnualWeekdayKey
): number {
  if (weekdayKey === 'sat') {
    // 토요는 WeekdayMaxPeriod에 키가 없음. 학사일정에서 수업일(class)로 열린 뒤에만 이 값이 쓰이므로
    // 평일 시수 상한의 최댓값을 쓴다.
    return Math.max(
      slotLimitByDay.mon ?? 0,
      slotLimitByDay.tue ?? 0,
      slotLimitByDay.wed ?? 0,
      slotLimitByDay.thu ?? 0,
      slotLimitByDay.fri ?? 0
    );
  }
  return slotLimitByDay[weekdayKey] ?? 0;
}

export function getSeedSlotKind(params: {
  dateIso: string;
  weekdayKey: AnnualWeekdayKey;
  slotIndex: number;
  sectionRows: ClassScheduleRow[];
  slotLimitByDay: WeekdayMaxPeriod;
  startsOn: string;
  endsOn: string;
  holidaySet: Set<string>;
  /**
   * 학사일정 `day_kind`. 토·일 수업일(`class`)이면 토/일 기본 공휴 취급하지 않음.
   * 미전달 시 학기 중 토/일(날짜)은 UI 기본 공휴(연간시간표 색) — 달력 DB에 자동 반영되지는 않음.
   */
  calendarDayKindByDate?: Record<string, DayKind>;
  /** 여름·겨울방학 구간이면 학기 안에서 자동 비수업(`non_class`) */
  isSchoolBreakDate?: (dateIso: string) => boolean;
}): AnnualTimetableSlotKind {
  const {
    dateIso,
    weekdayKey,
    slotIndex,
    sectionRows,
    slotLimitByDay,
    startsOn,
    endsOn,
    holidaySet,
    calendarDayKindByDate,
    isSchoolBreakDate,
  } = params;
  if (!dateIso || dateIso < startsOn || dateIso > endsOn) return 'out_of_term';
  if (isSchoolBreakDate?.(dateIso)) return 'holiday';
  if (holidaySet.has(dateIso)) return 'holiday';
  const noWeekendClassOverride = calendarDayKindByDate?.[dateIso] !== 'class';
  /** 토: 열 키 기준(연간 그리드에 토 열이 있을 때) */
  if (weekdayKey === 'sat' && noWeekendClassOverride) {
    return 'holiday';
  }
  /** 일: `dateIso`가 일요(연간 시간표는 일 열 없음 — 날짜만으로 기본 공휴) */
  const d = parseISODate(dateIso);
  if (!Number.isNaN(d.getTime()) && d.getDay() === 0 && noWeekendClassOverride) {
    return 'holiday';
  }
  const row = sectionRows[slotIndex];
  // 행 구분(activity/after_school)이 명시된 칸은 요일별 기준 시수보다 우선한다.
  // (예: 방과후3을 활동으로 둔 경우, weekdayMax 절단 때문에 non_class로 덮이지 않게)
  if (row?.kind === 'activity') return 'activity';
  if (row?.kind === 'after_school') return 'non_class';
  const slotLimit = getSlotLimitForDay(slotLimitByDay, weekdayKey);
  if (slotIndex >= slotLimit) return 'non_class';
  return 'class';
}

export function isEditableAnnualSlot(kind: AnnualTimetableSlotKind): boolean {
  return kind !== 'out_of_term' && kind !== 'holiday';
}

export function isDedicatedColor(colorHex: string, dedicatedColors: string[]): boolean {
  return Boolean(colorHex) && dedicatedColors.includes(colorHex);
}

export function isBaseExcludeColor(colorHex: string, baseExcludeColors: string[]): boolean {
  const normalized = normalizeColorHexForCompare(colorHex);
  if (!normalized) return false;
  return baseExcludeColors.some((c) => normalizeColorHexForCompare(c) === normalized);
}

/** 비교·그룹핑용: DB/피커에 따라 #RGB 대소문자가 달라져도 동일 색으로 취급 */
export function normalizeColorHexForCompare(colorHex: string): string {
  return (colorHex ?? '').trim().toLowerCase();
}

export function isFixedProgramColor(colorHex: string, regularColors: readonly string[]): boolean {
  const normalized = normalizeColorHexForCompare(colorHex);
  if (!normalized) return false;
  return regularColors.some((c) => normalizeColorHexForCompare(c) === normalized);
}

/**
 * 고정 프로그램 칸 판별.
 * 팔레트 색이 빠져도 고정 내용 행(연간 키·행사명)이 남아 있으면 고정으로 본다.
 */
/** 행사(고정) 프로그램은 고정 팔레트 색으로만 판별한다 */
export function annualSlotIsFixedProgram(params: {
  colorHex: string;
  regularColors: readonly string[];
  isProjectProgram?: boolean;
}): boolean {
  if (params.isProjectProgram) return false;
  return isFixedProgramColor(params.colorHex, params.regularColors);
}

export function buildAnnualCellMap(
  cells: AnnualTimetableCell[]
): Record<string, AnnualTimetableCell> {
  return cells.reduce<Record<string, AnnualTimetableCell>>((acc, cell) => {
    acc[buildAnnualCellKey(cell.grade_id, cell.monday_iso, cell.weekday_key, cell.slot_index)] = cell;
    return acc;
  }, {});
}

export function deriveAnnualEffectiveCell(params: {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeId: string;
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey;
  slotIndex: number;
  dateIso: string;
  sectionRows: ClassScheduleRow[];
  slotLimitByDay: WeekdayMaxPeriod;
  startsOn: string;
  endsOn: string;
  holidaySet: Set<string>;
  storedCell?: AnnualTimetableCell;
  dedicatedColors: string[];
  baseExcludeColors: string[];
  /** 있으면 토·일 수업일(`class`) 제외한 토요일 기본 공휴 판정에 사용 */
  calendarDayKindByDate?: Record<string, DayKind>;
  isSchoolBreakDate?: (dateIso: string) => boolean;
}): AnnualEffectiveCell {
  const {
    workspaceId,
    academicYear,
    termNumber,
    gradeId,
    mondayIso,
    weekdayKey,
    slotIndex,
    dateIso,
    sectionRows,
    slotLimitByDay,
    startsOn,
    endsOn,
    holidaySet,
    storedCell,
    dedicatedColors,
    baseExcludeColors,
    calendarDayKindByDate,
    isSchoolBreakDate,
  } = params;
  const seedKind = getSeedSlotKind({
    dateIso,
    weekdayKey,
    slotIndex,
    sectionRows,
    slotLimitByDay,
    startsOn,
    endsOn,
    holidaySet,
    calendarDayKindByDate,
    isSchoolBreakDate,
  });
  const { slotKind, nonClassSource } = resolveAnnualDisplaySlotKind(seedKind, storedCell?.slot_kind);
  /** 공휴일·학기 밖은 DB에 남은 과목명/색이 있어도 표시하지 않음(연간시간표 UI와 동일하게 비움). */
  const blankStoredTimetableFields = slotKind === 'out_of_term' || slotKind === 'holiday';
  const colorHex = blankStoredTimetableFields ? '' : (storedCell?.color_hex ?? '');
  const memo = blankStoredTimetableFields ? '' : (storedCell?.memo ?? '').trim();
  const textValue = blankStoredTimetableFields ? '' : (storedCell?.text_value ?? '');
  return {
    key: buildAnnualCellKey(gradeId, mondayIso, weekdayKey, slotIndex),
    id: storedCell?.id,
    workspaceId,
    gradeId,
    academicYear,
    termNumber,
    mondayIso,
    weekdayKey,
    slotIndex,
    dateIso,
    textValue,
    memo,
    colorHex,
    slotKind,
    nonClassSource,
    isEditable: isEditableAnnualSlot(slotKind),
    isDedicated: blankStoredTimetableFields
      ? false
      : (storedCell?.is_dedicated ?? isDedicatedColor(colorHex, dedicatedColors)),
    isBaseExcluded: blankStoredTimetableFields
      ? false
      : (storedCell?.is_base_excluded ?? isBaseExcludeColor(colorHex, baseExcludeColors)),
  };
}

export function createAnnualCellUpsert(params: {
  existingId?: string;
  workspaceId: string;
  gradeId: string;
  academicYear: number;
  termNumber: 1 | 2;
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey;
  slotIndex: number;
  textValue: string;
  memo?: string;
  colorHex: string;
  slotKind: AnnualTimetableSlotKind;
  dedicatedColors: string[];
  /** DB·UI에서 제외 색(단색)과 프로젝트 색(동일 hex+무늈)을 구분하는 플래그 */
  isBaseExcluded: boolean;
}): AnnualTimetableCellUpsert {
  const {
    existingId,
    workspaceId,
    gradeId,
    academicYear,
    termNumber,
    mondayIso,
    weekdayKey,
    slotIndex,
    textValue,
    memo,
    colorHex,
    slotKind,
    dedicatedColors,
    isBaseExcluded,
  } = params;
  // 신규 행은 id 키를 넣지 않아야 함. id: null/undefined가 PostgREST로 가면 DEFAULT가 적용되지 않아 NOT NULL 위반 발생.
  const row: AnnualTimetableCellUpsert = {
    workspace_id: workspaceId,
    grade_id: gradeId,
    academic_year: academicYear,
    term_number: termNumber,
    monday_iso: mondayIso,
    weekday_key: weekdayKey,
    slot_index: slotIndex,
    text_value: textValue.trim(),
    memo: memo?.trim() || null,
    color_hex: colorHex || null,
    slot_kind: slotKind,
    is_editable: isEditableAnnualSlot(slotKind),
    is_dedicated: isDedicatedColor(colorHex, dedicatedColors),
    is_base_excluded: isBaseExcluded,
  };
  if (existingId) {
    row.id = existingId;
  }
  return row;
}

export function buildProgramGrouping(cells: AnnualEffectiveCell[]): AnnualProgramGrouping {
  const byTextValue: Record<string, AnnualEffectiveCell[]> = {};
  const classCells = cells.filter((cell) => cell.slotKind === 'class');
  const countableTimetableCells = cells.filter(
    (cell) => cell.slotKind === 'class' || cell.slotKind === 'non_class'
  );
  countableTimetableCells.forEach((cell) => {
    const key = cell.textValue.trim();
    if (!key) return;
    if (!byTextValue[key]) byTextValue[key] = [];
    byTextValue[key].push(cell);
  });
  return {
    classCells,
    activityCells: cells.filter((cell) => cell.slotKind === 'activity'),
    nonClassCells: cells.filter((cell) => cell.slotKind === 'non_class'),
    dedicatedCells: cells.filter((cell) => cell.isDedicated),
    baseExcludedCells: cells.filter((cell) => cell.isBaseExcluded),
    byTextValue,
  };
}

export function getBaseGridCell(
  grid: BaseTimetableGrid | undefined,
  weekdayKey: AnnualWeekdayKey,
  slotIndex: number
) {
  if (!grid || weekdayKey === 'sat') return null;
  return grid[weekdayKey as WeekdayKey]?.[slotIndex] ?? null;
}
